import React, { useRef, useEffect, useState, ReactNode } from 'react';
import LeftRightNavigationButtons from './LeftRightNavigationButtons';

interface ScrollableContainerProps {
  children: ReactNode;
  className?: string;
  showNavigation?: boolean;
  scrollAmount?: number;
  navigationClassName?: string;
}

const ScrollableContainer: React.FC<ScrollableContainerProps> = ({
  children,
  className = '',
  showNavigation = true,
  scrollAmount = 300,
  navigationClassName = '',
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Check scroll position to enable/disable navigation buttons
  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  // Scroll left/right functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // Add scroll event listener
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkScrollPosition);
      checkScrollPosition();

      const resizeObserver = new ResizeObserver(checkScrollPosition);
      resizeObserver.observe(scrollContainer);

      return () => {
        scrollContainer.removeEventListener('scroll', checkScrollPosition);
        resizeObserver.disconnect();
      };
    }
  }, []);

  return (
    <div className="relative">
      <div
        ref={scrollContainerRef}
        className={`overflow-x-auto md:overflow-x-visible [&::-webkit-scrollbar]:hidden md:[&::-webkit-scrollbar]:auto ${className}`}
        style={{
          scrollbarWidth: 'none' /* Firefox */,
          msOverflowStyle: 'none' /* IE and Edge */,
        }}
      >
        {children}
      </div>

      {/* Navigation Buttons */}
      {showNavigation && (
        <LeftRightNavigationButtons
          hasPrev={canScrollLeft}
          hasNext={canScrollRight}
          onPrev={scrollLeft}
          onNext={scrollRight}
          className={`md:hidden ${navigationClassName}`}
        />
      )}
    </div>
  );
};

export default ScrollableContainer;
